import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import ScrollToTop from '@/components/feature/ScrollToTop';

const categoryImages: Record<string, string> = {
  football: 'https://readdy.ai/api/search-image?query=Professional%20soccer%20stadium%20filled%20with%20passionate%20fans%2C%20green%20pitch%20under%20floodlights%2C%20dramatic%20atmosphere%2C%20champions%20league%20style%2C%20editorial%20sports%20photography&width=800&height=450&seq=cat-football-hero&orientation=landscape',
  basketball: 'https://readdy.ai/api/search-image?query=NBA%20basketball%20arena%20with%20intense%20game%20action%2C%20players%20jumping%20for%20a%20dunk%2C%20bright%20arena%20lights%2C%20professional%20sports%20photography%2C%20dynamic%20composition&width=800&height=450&seq=cat-basketball-hero&orientation=landscape',
  tennis: 'https://readdy.ai/api/search-image?query=Professional%20tennis%20court%20with%20a%20player%20in%20mid%20swing%2C%20clay%20court%20surface%2C%20sunset%20lighting%2C%20elegant%20sports%20aesthetic%2C%20editorial%20photography&width=800&height=450&seq=cat-tennis-hero&orientation=landscape',
  esports: 'https://readdy.ai/api/search-image?query=Modern%20esports%20arena%20with%20gaming%20computers%20and%20giant%20LED%20screens%2C%20colorful%20neon%20lighting%2C%20professional%20tournament%20setup%2C%20futuristic%20atmosphere&width=800&height=450&seq=cat-esports-hero&orientation=landscape',
  comprehensive: 'https://readdy.ai/api/search-image?query=Olympic%20style%20multi%20sport%20stadium%20collage%2C%20athletes%20from%20different%20sports%2C%20golden%20hour%20lighting%2C%20inspiring%20sports%20photography%2C%20clean%20composition&width=800&height=450&seq=cat-comprehensive-hero&orientation=landscape',
};

const categoryLabels: Record<string, string> = {
  football: '足球',
  basketball: '篮球',
  tennis: '网球',
  esports: '电竞',
  comprehensive: '综合体育',
};

const categoryArticleImages: Record<number, string> = {
  1: 'https://readdy.ai/api/search-image?query=Chinese%20national%20soccer%20team%20in%20an%20important%20World%20Cup%20qualifying%20match%2C%20players%20in%20red%20jerseys%2C%20intense%20competition%2C%20stadium%20atmosphere&width=800&height=450&seq=cat-art-football-1&orientation=landscape',
  6: 'https://readdy.ai/api/search-image?query=Premier%20League%20soccer%20match%20between%20Manchester%20City%20and%20Liverpool%2C%20fast%20paced%20action%2C%20players%20in%20blue%20and%20red%2C%20electric%20stadium&width=800&height=450&seq=cat-art-football-2&orientation=landscape',
  11: 'https://readdy.ai/api/search-image?query=Champions%20League%20group%20stage%20draw%20ceremony%20in%20Monaco%2C%20elegant%20venue%2C%20football%20club%20representatives%2C%20prestigious%20atmosphere&width=800&height=450&seq=cat-art-football-3&orientation=landscape',
  2: 'https://readdy.ai/api/search-image?query=Lakers%20vs%20Warriors%20NBA%20game%2C%20basketball%20players%20in%20intense%20competition%2C%20packed%20arena%2C%20professional%20sports%20photography&width=800&height=450&seq=cat-art-basketball-1&orientation=landscape',
  7: 'https://readdy.ai/api/search-image?query=CBA%20Chinese%20basketball%20league%20game%2C%20players%20in%20action%2C%20Chinese%20sports%20arena%2C%20enthusiastic%20crowd%2C%20professional%20photography&width=800&height=450&seq=cat-art-basketball-2&orientation=landscape',
  3: 'https://readdy.ai/api/search-image?query=Wimbledon%20gentlemens%20singles%20final%20trophy%20ceremony%2C%20tennis%20player%20holding%20golden%20trophy%20on%20center%20court%2C%20prestigious%20moment&width=800&height=450&seq=cat-art-tennis-1&orientation=landscape',
  10: 'https://readdy.ai/api/search-image?query=Young%20female%20tennis%20player%20celebrating%20French%20Open%20victory%20on%20Roland%20Garros%20clay%20court%2C%20emotional%20moment%2C%20golden%20sunlight&width=800&height=450&seq=cat-art-tennis-2&orientation=landscape',
  4: 'https://readdy.ai/api/search-image?query=League%20of%20Legends%20worlds%20tournament%20stage%2C%20LPL%20team%20competing%2C%20grand%20arena%20with%20thousands%20of%20fans%2C%20epic%20esports%20atmosphere&width=800&height=450&seq=cat-art-esports-1&orientation=landscape',
  9: 'https://readdy.ai/api/search-image?query=DOTA2%20The%20International%20tournament%2C%20Chinese%20team%20on%20stage%2C%20giant%20screen%20showing%20gameplay%2C%20passionate%20audience%2C%20professional%20esports&width=800&height=450&seq=cat-art-esports-2&orientation=landscape',
  5: 'https://readdy.ai/api/search-image?query=Chinese%20table%20tennis%20team%20celebrating%20world%20championship%20victory%2C%20gold%20medals%2C%20national%20pride%2C%20professional%20indoor%20sports%20photography&width=800&height=450&seq=cat-art-comp-1&orientation=landscape',
  8: 'https://readdy.ai/api/search-image?query=Formula%201%20race%20car%20speeding%20on%20Hungaroring%20track%20in%20rainy%20conditions%2C%20water%20spray%2C%20dramatic%20motorsport%20photography%2C%20cinematic&width=800&height=450&seq=cat-art-comp-2&orientation=landscape',
  12: 'https://readdy.ai/api/search-image?query=Chinese%20swimmers%20competing%20at%20world%20championships%20in%20Budapest%2C%20Olympic%20pool%2C%20dynamic%20swimming%20action%2C%20professional%20aquatics%20photography&width=800&height=450&seq=cat-art-comp-3&orientation=landscape',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();

  const currentCategory = categories.find((c) => c.slug === slug);
  const categoryName = slug ? categoryLabels[slug] || slug : '';
  const filteredArticles = articles.filter((a) => a.category === slug);

  if (!currentCategory) {
    return (
      <>
        <SEOHead title="分类未找到 - 开云体育" />
        <ScrollToTop />
        <Navbar />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <p className="text-foreground-600">分类未找到</p>
            <Link to="/" className="text-primary-600 hover:text-primary-700 mt-4 inline-block">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEOHead
        title={`${currentCategory.name} - 开云体育 | ${currentCategory.name}赛事资讯与最新动态`}
        description={currentCategory.description}
        keywords={`开云体育,${currentCategory.name},${currentCategory.name}赛事,${currentCategory.name}资讯,体育新闻`}
        canonicalPath={`/category/${slug}`}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumb
              items={[
                { label: t('nav.categories') },
                { label: currentCategory.name },
              ]}
            />
            <div className="flex flex-col lg:flex-row gap-6">
              <div className="lg:w-64 flex-shrink-0">
                <div className="bg-background-50 border border-background-200/60 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-foreground-900 mb-3 font-heading">
                    {t('nav.categories')}
                  </h3>
                  <div className="space-y-1">
                    {categories.map((cat) => (
                      <Link
                        key={cat.slug}
                        to={`/category/${cat.slug}`}
                        className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors whitespace-nowrap ${
                          cat.slug === slug
                            ? 'bg-primary-50 text-primary-600 font-medium'
                            : 'text-foreground-600 hover:bg-background-100'
                        }`}
                      >
                        <i className={`${cat.icon} text-sm`}></i>
                        {cat.name}
                        <span className="ml-auto text-xs text-foreground-400">{cat.articleCount}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex-1">
                <div className="bg-background-50 border border-background-200/60 rounded-lg p-6 mb-6">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="w-10 h-10 flex items-center justify-center bg-primary-100 text-primary-600 rounded-lg">
                      <i className={`${currentCategory.icon} text-lg`}></i>
                    </span>
                    <h1 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">
                      {currentCategory.name}赛事资讯
                    </h1>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {currentCategory.description}
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                  {filteredArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.id}`}
                      className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/60 transition-all duration-300 hover:scale-[1.01] cursor-pointer"
                    >
                      <div className="relative w-full h-44 overflow-hidden">
                        <img
                          src={categoryArticleImages[article.id] || categoryImages[slug || 'football']}
                          alt={article.title}
                          title={article.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm md:text-base font-semibold text-foreground-900 mb-2 line-clamp-2 font-heading group-hover:text-primary-600 transition-colors">
                          {article.title}
                        </h3>
                        <p className="text-xs text-foreground-600 line-clamp-2 mb-3">
                          {article.summary}
                        </p>
                        <div className="flex items-center justify-between text-xs text-foreground-500">
                          <span>{article.publishedAt}</span>
                          <span className="flex items-center gap-1">
                            <i className="ri-eye-line"></i>
                            {article.readCount >= 10000 ? `${(article.readCount / 10000).toFixed(1)}万` : article.readCount}
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>

                {filteredArticles.length === 0 && (
                  <div className="text-center py-20">
                    <p className="text-foreground-500">该分类暂无文章</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}